import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import flagsmith from '@flagsmith/flagsmith';
import * as Sentry from '@sentry/react';

import type { ILead } from 'src/services/api';
import {
    Auth,
    useAchievements,
    useConversations,
    useLeads,
    useLoggedInUser,
    useNotifications,
    useProperties,
    isNonLeasedPropertyNotification,
} from 'src/services/api';

import { normalizeSource } from 'src/pages/landlord/analytics/utils';
import LandlordLayout from 'src/components/layout/landlord/LandlordLayout';
import ServerError from 'src/components/errors/ServerError';
import { AuthenticatedStateContext } from 'src/authenticated-state/context';
import { usePersistentState } from 'src/services/storage/use-persistent-state';
import { Config } from 'src/config';
import { useMounted } from 'src/hooks/use-mounted';
import { ensureSentryReplayRecording } from 'src/utils/start-sentry-replay';

type Props = {
  children: React.ReactNode;
};

/**
 * TODO: replace with feature flag + segment in subsequent PR
 * e.g. const flags = useFlags([EFeatureFlag.SENTRY_SESSION_REPLAY]);
 */
const REPLAY_USER_IDS = (import.meta.env.VITE_SENTRY_REPLAY_USER_IDS ?? '')
    .split(',')
    .map(Number);
const REPLAY_ALL_PATH_USER_IDS = (
    import.meta.env.VITE_SENTRY_REPLAY_ALL_PATH_USER_IDS ?? ''
)
    .split(',')
    .map(Number);
const REPLAY_PATH = import.meta.env.VITE_SENTRY_REPLAY_PATH;
const REPLAY_SAMPLE_RATE = Number(
    import.meta.env.VITE_SENTRY_REPLAY_SAMPLE_RATE ?? 0,
);

export default function AuthenticatedStateProvider({ children }: Props) {
    const isLoggedIn = !!Auth.accessToken;
    const init = useRef(false);
    const isSentryReplaySampled = useRef<boolean | null>(null);

    const navigate = useNavigate();
    const location = useLocation();
    // showArchived is a leads page concern only — do not use it to filter conversations.
    const [showArchived, setShowArchived] = useState(false);
    const [isInactivePropertiesModalClosed, setIsInactivePropertiesModalClosed] =
    usePersistentState('inactivePropertiesClosed', false);

    const mounted = useMounted();

    const { data: loggedInUser, isError: isLoggedInUserError } =
    useLoggedInUser();
    const userSettings = loggedInUser?.settings;
    const { data: achievements } = useAchievements({ enabled: mounted });
    const jwtPayload = Auth.jwtPayload;
    const isLoginAs = jwtPayload?.dl === 1;

    useEffect(() => {
        if (!loggedInUser?.uuid || loggedInUser.leasing_team_id == null) {
            return;
        }

        Sentry.setUser({
            id: isLoginAs ? 'delegated-login' : loggedInUser.uuid,
        });
        Sentry.setTag(
            'leasing_team_id',
            String(loggedInUser.leasing_team_id),
        );
        Sentry.setTag('is_login_as', isLoginAs);
    }, [isLoginAs, loggedInUser?.uuid, loggedInUser?.leasing_team_id]);

    const isBypassPaywall = jwtPayload?.ibp;
    const isPaywallEnabled = !isBypassPaywall && !loggedInUser?.team?.is_active;

    // Kept here to seed the query cache and drive refetches (e.g. on showArchived change).
    // Not exposed in context — use useConversations() directly.
    const {
        data: conversations,
        refetch: refetchConversations,
        isLoading: isConversationLoading,
        isFetched: isConversationFetched,
    } = useConversations({
        disabled: !loggedInUser?.is_onboarding_complete || isPaywallEnabled,
    });

    const {
        data: leads,
        dataUpdatedAt: leadsUpdatedAt,
        refetch: refetchLeads,
        isLoading: isLeadsLoading,
    } = useLeads({
        disabled: !loggedInUser?.is_onboarding_complete || isPaywallEnabled,
        isArchived: showArchived ? true : false,
    });
    const leadIngestionSources = useMemo(() => {
        if (!leads) {
            return [];
        }

        const sources = new Set<string>();
        leads.forEach((lead) => {
            sources.add(normalizeSource(lead.ingestion_source));
        });

        return Array.from(sources).sort();
    }, [leads]);

    const leadsByUuid = useMemo(() => {
        if (!leads) {
            return new Map();
        }

        return leads.reduce((acc, lead) => {
            acc.set(lead.uuid, lead);
            return acc;
        }, new Map<string, ILead>());
    }, [leads]);

    // prefetch properties
    const { data: properties } = useProperties({
        disabled: !loggedInUser?.is_onboarding_complete || isPaywallEnabled,
    });

    useEffect(() => {
        const onLogout = () => {
            flagsmith.logout();
            Sentry.setUser(null);
            Sentry.setTag('leasing_team_id', undefined);
            Sentry.setTag('is_login_as', undefined);
        };
        document.addEventListener('reffie_logout', onLogout);
        return () => document.removeEventListener('reffie_logout', onLogout);
    }, []);

    useEffect(() => {
        if (!loggedInUser?.id || !loggedInUser.uuid) {
            return;
        }
        if (!loggedInUser?.team) {
            return;
        }

        /**
     * Using uuid instead of id because helps quickly differentiate between local and non-local users
     */
        flagsmith.identify(loggedInUser.uuid, {
            user_uuid: loggedInUser.uuid,
            user_id: loggedInUser.id,
            leasing_team_id: loggedInUser.leasing_team_id,
            leasing_team_name: loggedInUser.team.name,
            ...(loggedInUser.team.org_id !== null && {
                org_id: loggedInUser.team.org_id,
            }),
            /**
       * Helps differentiate between local users running against staging/prod vs actual users in those environments
       */
            is_local_dev: Config.isLocal,
        });
    }, [
        loggedInUser?.uuid,
        loggedInUser?.id,
        loggedInUser?.leasing_team_id,
        loggedInUser?.team,
    ]);

    useEffect(() => {
        if (!loggedInUser) {
            return;
        }

        const isTargetedUser =
      REPLAY_USER_IDS.includes(loggedInUser.id) &&
      location.pathname.startsWith(REPLAY_PATH);
        const isAllPathUser = REPLAY_ALL_PATH_USER_IDS.includes(loggedInUser.id);

        if (isSentryReplaySampled.current === null) {
            isSentryReplaySampled.current = Math.random() < REPLAY_SAMPLE_RATE;
        }

        // All-path users record their whole session; the sample rate applies
        // only to the path-scoped cohort.
        if (isAllPathUser || (isTargetedUser && isSentryReplaySampled.current)) {
            ensureSentryReplayRecording();
        }
    }, [loggedInUser, location.pathname]);

    // Refetch leads and conversations when showArchived changes
    useEffect(() => {
        if (!Auth.accessToken) {
            return;
        }

        if (!loggedInUser?.is_onboarding_complete) {
            return;
        }

        if (!init.current) {
            init.current = true;
            return;
        }

        refetchLeads();
        refetchConversations();
    }, [loggedInUser, showArchived, refetchLeads, refetchConversations]);

    const { data: notifications } = useNotifications(!isLoggedIn || !mounted);

    const isInactivePropertiesModalOpen = useMemo(() => {
        if (
            isInactivePropertiesModalClosed ||
      !notifications?.length ||
      !properties
        ) {
            return false;
        }

        const validNotifications = notifications
            .filter((notification) => !notification.is_read_by_user)
            .filter(isNonLeasedPropertyNotification)
            .filter((notification) => {
                const property = properties.find(
                    (p) => p.id === notification.payload.property_id,
                );
                return !!property;
            });

        return validNotifications.length > 0;
    }, [isInactivePropertiesModalClosed, notifications, properties]);

    if (!Auth.accessToken) {
        navigate('/landlord/login');
        return null;
    }

    /**
   * `/me` failed for a reason that is not the session (a 5xx, a dead network)
   * and has run out of retries. There is no user to render, so say so rather
   * than spin on the loading layout forever. An expired session never reaches
   * here: Auth clears it and redirects, which the accessToken check above
   * turns into the navigate to login.
   */
    if (isLoggedInUserError) {
        return (
            <LandlordLayout>
                <ServerError />
            </LandlordLayout>
        );
    }

    if (!loggedInUser || !userSettings) {
        return <LandlordLayout isLoading />;
    }

    if (!loggedInUser?.is_onboarding_complete) {
        return <Navigate to="/landlord/onboard" replace />;
    }

    const areLeadsOrConversationsLoading =
    !isPaywallEnabled && (!leads || !conversations);
    const isAuthenticatedDataLoading =
    !loggedInUser ||
    areLeadsOrConversationsLoading ||
    isLeadsLoading ||
    isConversationLoading ||
    !isConversationFetched;
    const isLoading = isLoggedIn && isAuthenticatedDataLoading;
    return (
        <AuthenticatedStateContext.Provider
            value={{
                showArchived,
                setShowArchived,

                loggedInUser,
                userSettings,

                leads: leads || [],
                leadsUpdatedAt,
                leadsByUuid,
                leadIngestionSources,

                notifications: notifications || [],
                achievements: achievements || {
                    achievements: [],
                    user_achievements: [],
                },

                isPaywallEnabled,

                isInactivePropertiesModalOpen,
                setIsInactivePropertiesModalClosed,

                isLoading,
            }}
        >
            {isLoading || !loggedInUser ? <LandlordLayout isLoading /> : children}
        </AuthenticatedStateContext.Provider>
    );
}
